import { RuzcalMgmtPage } from './mgmt-page';
import {
  IcoCalendar,
  IcoCheck,
  IcoChevronDown,
  IcoChevronRight,
  IcoClock,
  IcoDotsVertical,
  IcoExternalLink,
  IcoEye,
  IcoFolder,
  IcoHide,
  IcoMapPin,
  IcoPencilSquare,
  IcoPlus,
  IcoShare,
} from '@components/icons';
import { ListAddAction, PageContent, PageHeading, PageSection, defCalRoute } from './common';
import { rpx } from 'client/lib/rpx-client';
import { BtnCopy, Button } from '@components/buttons';
import { ruzcal } from 'shared/urls';
import { LoadedProps } from '@components/router';
import { SummarizeAvailableTimes } from './availability-summary';
import { useEffect, useRef, useState } from 'preact/hooks';
import { showAddCategoriesModal } from './cal-categories-add';
import { EventTypeCategoryRow, EventTypeRow } from 'server/types/cal-schema';
import { showToast } from '@components/toaster';
import { Dropdown, MenuItem } from '@components/dropdown';
import { Pill } from '@components/pill';
import { autoFocusSelf } from 'client/utils/autofocus';

export const route = defCalRoute({ authLevel: 'guide', load, Page });

async function load() {
  const [eventTypes, availability, host, eventTypeCategories] = await Promise.all([
    rpx.ruzcalEventTypes.getEventTypesList(),
    rpx.ruzcal.getAvailabilityList(),
    rpx.ruzcal.getHost({}),
    rpx.ruzcalEventTypes.getEventTypeCategoriesList(),
  ]);
  return { eventTypes, availability, urlPrefix: host!.urlPrefix, eventTypeCategories };
}

type Data = Awaited<ReturnType<typeof load>>;

export function summarizeDuration(duration: number) {
  return `${duration}m`;
}

function EditLink(
  props: Pick<
    EventTypeRow,
    'id' | 'name' | 'isPrivate' | 'duration' | 'location' | 'locationDetail'
  > & { href: string } & {
    availability?: Data['availability'][0];
    timezone: string;
  },
) {
  const fullUrl = `${window.location.origin}${props.href}`;

  return (
    <div class="rounded-md flex justify-between items-start">
      <span class="flex-grow border rounded-2xl overflow-hidden">
        <a
          href={`/calendar/event-types/${props.id}`}
          class="flex flex-col p-4 gap-1 hover:bg-gray-100 transition-all text-inherit relative"
        >
          <span class="font-semibold text-base">{props.name}</span>
          <span class="hidden text-inherit sm:inline-flex items-center gap-3 absolute right-6 top-6 border border-gray-300 rounded-full p-6 py-1 hover:border-gray-400 transition-all">
            <IcoPencilSquare class="size-4 stroke-2" />
            Edit
          </span>
          <footer class="grid grid-cols-2 gap-2 sm:flex sm:gap-8 items-center">
            <span class="inline-flex items-center gap-2">
              <IcoClock /> {summarizeDuration(props.duration)}
            </span>
            {props.availability && (
              <span class="inline-flex items-center gap-2">
                <IcoCalendar />
                <SummarizeAvailableTimes
                  renderTimeZone={props.timezone}
                  scheduleTimeZone={props.availability.timezone}
                  timeslots={props.availability.timeslots}
                />
              </span>
            )}
            <span class="inline-flex items-center gap-2 capitalize">
              <IcoMapPin /> {props.location}
            </span>
            {props.isPrivate && (
              <span class="inline-flex items-center gap-2">
                <IcoHide /> Not publicly listed
              </span>
            )}
            {!props.isPrivate && (
              <span class="inline-flex items-center gap-2">
                <IcoEye /> Publicly listed
              </span>
            )}
          </footer>
        </a>
        <span class="flex items-center border-t">
          <BtnCopy
            value={fullUrl}
            copiedText="Booking link copied"
            class="hover:bg-gray-200 transition-all rounded-none rounded-bl-2xl p-2 px-4 gap-2"
            margin=" "
            icon={<IcoShare />}
          >
            Share
          </BtnCopy>
          <Button
            href={fullUrl}
            target="_blank"
            class="hover:bg-gray-200 transition-all rounded-none flex items-center gap-2 text-inherit p-2 px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <IcoExternalLink />
            View
          </Button>
        </span>
      </span>
    </div>
  );
}

type EventTypeCategoryWithEvents = EventTypeCategoryRow & {
  eventTypes: EventTypeRow[];
};
function Page(props: LoadedProps<typeof load>) {
  const { eventTypes, availability, urlPrefix, eventTypeCategories } = props.state;
  const [openCategory, setOpenCategory] = useState<Record<string, boolean>>({});
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editedCategoryName, setEditedCategoryName] = useState<string>('');

  // Create the merged and sorted list
  // Event Types with No CategoryId's will be grouped under default category
  const categorizedEventTypes: EventTypeCategoryWithEvents[] = eventTypeCategories
    .map((category) => ({
      ...category,
      eventTypes: eventTypes.filter((event) =>
        event.categoryId ? event.categoryId === category.id : category.isDefault,
      ),
    }))
    .sort((a, b) => b.eventTypes.length - a.eventTypes.length); // Descending sort

  const toggleCategory = (id: string) => {
    setOpenCategory((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // To expand first category by default
  const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current && categorizedEventTypes.length > 0) {
      setOpenCategory({ [categorizedEventTypes[0].id]: true });
      initialized.current = true;
    }
  }, [categorizedEventTypes]);

  const handleCategoryRename = (category: EventTypeCategoryRow) => {
    setEditingCategoryId(category.id);
    setEditedCategoryName(category.name);
  };

  const saveCategory = async (
    updatedCategory: EventTypeCategoryRow,
    action: 'rename' | 'set-default',
  ) => {
    await rpx.ruzcalEventTypes.saveEventTypeCategory({
      id: updatedCategory.id,
      name: updatedCategory.name,
      isDefault: updatedCategory.isDefault,
    });

    // Fetch updated categories
    const updatedCategoriesList = await rpx.ruzcalEventTypes.getEventTypeCategoriesList();
    // Replace local state with updated categories list
    props.setState((s: any) => ({
      ...s,
      eventTypeCategories: updatedCategoriesList,
    }));

    if (action === 'rename') {
      setEditingCategoryId(null);
      setEditedCategoryName('');
    }
  };

  return (
    <RuzcalMgmtPage title="Event Types" currentPage="eventtypes">
      <PageContent>
        <PageSection>
          <PageHeading
            title="Event types"
            subtitle="Manage the types of events that people can book."
            action={
              <div class="flex gap-2 items-center justify-end">
                <Button
                  class="p-2 inline-flex items-center  rounded-full transition-all hover:bg-gray-100 gap-2 border"
                  onClick={async () => {
                    const result = await showAddCategoriesModal();
                    if (result) {
                      showToast({
                        type: 'ok',
                        title: 'New event category created',
                        message: (
                          <span>
                            New Event category <span class="font-bold">{result.name}</span> has been
                            added to event categories list.
                          </span>
                        ),
                      });
                      // Fetch updated categories
                      const updatedCategoriesList =
                        await rpx.ruzcalEventTypes.getEventTypeCategoriesList();
                      props.setState((s: any) => ({
                        ...s,
                        eventTypeCategories: updatedCategoriesList,
                      }));
                    }
                  }}
                >
                  <IcoPlus />
                  Add New Category
                </Button>
                <ListAddAction href="/calendar/event-types/new" content="Add event type" />
              </div>
            }
          />

          <div class="rounded-lg shadow-sm divide-y text-gray-900 whitespace-normal">
            {categorizedEventTypes.length > 0 && (
              <>
                {categorizedEventTypes.map((category) => (
                  <div key={category.id} class="border shadow-xl rounded-lg">
                    <div class="flex items-center bg-gray-50 p-2 hover:bg-gray-100 gap-4">
                      <div class="flex items-center" onClick={() => toggleCategory(category.id)}>
                        {openCategory[category.id] ? (
                          <IcoChevronDown class="w-5 h-5 text-gray-500 mr-2 transition-transform duration-200" />
                        ) : (
                          <IcoChevronRight class="w-5 h-5 text-gray-500 mr-2 transition-transform duration-200" />
                        )}
                        <IcoFolder class="w-6 h-6 text-indigo-500 mr-3" />
                      </div>
                      <div class="grow flex items-center gap-2">
                        {editingCategoryId === category.id ? (
                          <div class="flex items-center gap-2">
                            <input
                              ref={autoFocusSelf}
                              value={editedCategoryName}
                              onInput={(e) =>
                                setEditedCategoryName((e.target as HTMLInputElement).value)
                              }
                              onBlur={() => {
                                const { eventTypes, ...eventTypeCategoryRow } = category;
                                const updatedCategory = {
                                  ...eventTypeCategoryRow,
                                  name: editedCategoryName,
                                };
                                saveCategory(updatedCategory, 'rename');
                              }}
                              onKeyDown={(e: any) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                }
                              }}
                              class="border rounded px-2 py-1 text-sm w-auto"
                            />
                          </div>
                        ) : (
                          <>
                            {category.name}
                            {category.isDefault && (
                              <Pill color="green" centered>
                                default
                              </Pill>
                            )}
                          </>
                        )}
                      </div>
                      <div class="mt-4 flex items-center justify-end sm:mt-0 sm:ml-6 sm:shrink-0 sm:justify-start">
                        <span class="inline-flex items-center rounded-full bg-indigo-50 px-2 py-1 text-xs font-light text-indigo-500 ring-1 ring-indigo-500/20 ring-inset">
                          {category.eventTypes.length} Event Types
                        </span>
                        <div class="max-w-full items-center justify-center inline-flex flex-col  ml-3">
                          <Dropdown
                            noPadding
                            hideDownIcon={true}
                            fullWidth={true}
                            triggerClass="max-w-full"
                            renderMenu={() => (
                              <>
                                <MenuItem
                                  onClick={() => handleCategoryRename(category)}
                                  regularCase
                                >
                                  <IcoPencilSquare />
                                  Rename Category
                                </MenuItem>
                                {!category.isDefault ? (
                                  <MenuItem
                                    onClick={() => {
                                      const { eventTypes, ...eventTypeCategoryRow } = category;
                                      const updatedCategory = {
                                        ...eventTypeCategoryRow,
                                        isDefault: true,
                                      };
                                      saveCategory(updatedCategory, 'set-default');
                                    }}
                                    regularCase
                                  >
                                    <IcoCheck />
                                    Set as default category
                                  </MenuItem>
                                ) : (
                                  ''
                                )}
                              </>
                            )}
                          >
                            <IcoDotsVertical class="w-5 h-5 text-gray-500" />
                          </Dropdown>
                        </div>
                      </div>
                    </div>
                    {openCategory[category.id] && (
                      <div class="px-12 py-4 space-y-4 font-normal">
                        {category.eventTypes.length > 0 ? (
                          <>
                            {category.eventTypes.map((x) => (
                              <EditLink
                                key={x.id}
                                timezone={props.auth.user!.timezone}
                                id={x.id}
                                name={x.name}
                                href={ruzcal.newBookingUrl({ urlPrefix, urlSuffix: x.urlSuffix })}
                                duration={x.duration}
                                location={x.location}
                                locationDetail={x.locationDetail}
                                isPrivate={x.isPrivate}
                                availability={availability.find((a) => a.id === x.availabilityId)}
                              />
                            ))}
                          </>
                        ) : (
                          <p class="text-sm text-gray-500">
                            No event types are currently in this category. You can move existing
                            events into this category, or &nbsp;
                            <a
                              href={`/calendar/event-types/new?categoryId=${category.id}`}
                              class="text-indigo-600 hover:underline"
                            >
                              create a new event type.
                            </a>
                            .
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </PageSection>
      </PageContent>
    </RuzcalMgmtPage>
  );
}
